import React, { memo, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RessourceApi from '../api/RessourceApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 1),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 1),
    },
  },
  card: {
    borderRadius: 10,
    border: '1px solid #dbe8f2',
    boxShadow: '0 10px 24px rgba(8, 36, 56, 0.08)',
    background: '#fff',
    padding: theme.spacing(2),
    height: '100%',
  },
  title: {
    marginTop: 0,
    marginBottom: theme.spacing(0.5),
    color: '#0f3247',
    fontWeight: 800,
  },
  subtitle: {
    marginTop: 0,
    marginBottom: theme.spacing(2),
    color: '#476070',
  },
  categoryField: {
    marginBottom: theme.spacing(1.5),
    maxWidth: 340,
  },
  tableWrap: {
    borderRadius: 10,
    border: '1px solid #dbe8f2',
    overflow: 'hidden',
  },
  empty: {
    padding: theme.spacing(2),
    color: '#5d7280',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  actionIcon: {
    border: '1px solid #dbe8f2',
    borderRadius: 8,
  },
  editor: {
    marginTop: theme.spacing(2),
    border: '1px solid #dbe8f2',
    borderRadius: 10,
    padding: theme.spacing(2),
    background: '#f9fcff',
  },
}));

const categories = [
  { value: 'Actualite', label: 'Actualite' },
  { value: 'Annonce', label: 'Annonce' },
  { value: 'Evenement', label: 'Evenement' },
  { value: 'Pratique', label: 'Pratique' },
  { value: 'Presentation', label: 'Presentation' },
  { value: 'Navigation', label: 'Accueil' },
];

const initialEdition = {
  id: '',
  titre: '',
  resume: '',
  description: '',
  idImage: '',
  cheminFichier: '',
  estActif: true,
};

const BanqueRessourcesContainer = memo(() => {
  const classes = useStyles();
  const [categorieSelectionnee, setCategorieSelectionnee] = useState('Actualite');
  const [ressources, setRessources] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [messageErreur, setMessageErreur] = useState('');
  const [messageSucces, setMessageSucces] = useState('');
  const [edition, setEdition] = useState(initialEdition);
  const [enregistrementEnCours, setEnregistrementEnCours] = useState(false);

  const aEditionActive = useMemo(() => Boolean(edition.id), [edition.id]);

  const chargerRessources = async () => {
    setMessageErreur('');
    setMessageSucces('');
    setChargement(true);

    try {
      const data = await RessourceApi.obtenirRessourcesParCategorie(categorieSelectionnee);
      setRessources(Array.isArray(data) ? data : []);
    } catch (error) {
      setRessources([]);
      setMessageErreur("Impossible de charger les ressources de cette categorie.");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    setEdition(initialEdition);
    chargerRessources();
  }, [categorieSelectionnee]);

  const lancerEdition = (ressource) => {
    setMessageErreur('');
    setMessageSucces('');
    setEdition({
      id: ressource?.id || '',
      titre: ressource?.titre || '',
      resume: ressource?.resume || '',
      description: ressource?.description || '',
      idImage: ressource?.idImage || '',
      cheminFichier: ressource?.cheminFichier || '',
      estActif: Boolean(ressource?.estActif),
    });
  };

  const annulerEdition = () => {
    setEdition(initialEdition);
  };

  const sauvegarderEdition = async () => {
    if (!edition.id) {
      return;
    }

    if (!edition.titre.trim() || !edition.description.trim()) {
      setMessageErreur('Le titre et la description sont obligatoires.');
      return;
    }

    setEnregistrementEnCours(true);
    setMessageErreur('');
    setMessageSucces('');

    try {
      await RessourceApi.modifier(edition.id, {
        titre: edition.titre.trim(),
        resume: edition.resume.trim(),
        description: edition.description.trim(),
        idImage: edition.idImage,
        categorie: categorieSelectionnee,
        cheminFichier: edition.cheminFichier,
        estActif: edition.estActif,
      });
      setMessageSucces('Ressource modifiee avec succes.');
      setEdition(initialEdition);
      await chargerRessources();
    } catch (error) {
      setMessageErreur('Echec de la modification de la ressource.');
    } finally {
      setEnregistrementEnCours(false);
    }
  };

  const supprimerRessource = async (ressource) => {
    if (!ressource?.id) {
      return;
    }

    const confirme = window.confirm(`Supprimer la ressource "${ressource.titre || 'Sans titre'}" ?`);
    if (!confirme) {
      return;
    }

    setMessageErreur('');
    setMessageSucces('');

    try {
      await RessourceApi.supprimer(ressource.id);
      setMessageSucces('Ressource supprimee avec succes.');
      if (edition.id === ressource.id) {
        setEdition(initialEdition);
      }
      await chargerRessources();
    } catch (error) {
      setMessageErreur('Echec de la suppression de la ressource.');
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.card} elevation={0}>
        <h2 className={classes.title}>Pilotage des ressources par categorie</h2>
        <p className={classes.subtitle}>Selectionnez une categorie, puis modifiez ou supprimez une ressource depuis le tableau.</p>
        <Divider style={{ marginBottom: 16 }} />

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12}>
            <TextField
              select
              label="Categorie"
              value={categorieSelectionnee}
              onChange={(event) => setCategorieSelectionnee(event.target.value)}
              className={classes.categoryField}
              fullWidth
            >
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </TextField>

            {messageSucces && <Alert severity="success" style={{ marginBottom: 12 }}>{messageSucces}</Alert>}
            {messageErreur && <Alert severity="error" style={{ marginBottom: 12 }}>{messageErreur}</Alert>}

            <TableContainer className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Titre</TableCell>
                    <TableCell>Resume</TableCell>
                    <TableCell align="center">Etat</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!chargement && ressources.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className={classes.empty}>
                        Aucune ressource dans cette categorie.
                      </TableCell>
                    </TableRow>
                  )}

                  {ressources.map((ressource) => (
                    <TableRow key={ressource.id || `${ressource.titre}-${ressource.resume}`} hover>
                      <TableCell>{ressource.titre || 'Sans titre'}</TableCell>
                      <TableCell>{ressource.resume || '-'}</TableCell>
                      <TableCell align="center">
                        {ressource.estActif ? <Chip size="small" color="success" label="Actif" /> : <Chip size="small" label="Inactif" />}
                      </TableCell>
                      <TableCell align="right">
                        <div className={classes.actions}>
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              className={classes.actionIcon}
                              color="primary"
                              onClick={() => lancerEdition(ressource)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              className={classes.actionIcon}
                              color="error"
                              onClick={() => supprimerRessource(ressource)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {aEditionActive && (
              <div className={classes.editor}>
                <Typography variant="h6" style={{ marginBottom: 12 }}>Modifier la ressource</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Titre"
                      value={edition.titre}
                      onChange={(event) => setEdition((prev) => ({ ...prev, titre: event.target.value.slice(0, 50) }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Resume"
                      value={edition.resume}
                      onChange={(event) => setEdition((prev) => ({ ...prev, resume: event.target.value.slice(0, 120) }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      label="Description"
                      value={edition.description}
                      onChange={(event) => setEdition((prev) => ({ ...prev, description: event.target.value.slice(0, 5000) }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={Boolean(edition.estActif)}
                          onChange={(event) => setEdition((prev) => ({ ...prev, estActif: event.target.checked }))}
                        />
                      )}
                      label="Ressource active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.actions}>
                      <Button variant="contained" onClick={sauvegarderEdition} disabled={enregistrementEnCours}>
                        {enregistrementEnCours ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                      <Button variant="outlined" onClick={annulerEdition} disabled={enregistrementEnCours}>
                        Annuler
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </div>
            )}
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
});

export default BanqueRessourcesContainer;
